import { action, computed, makeObservable, observable } from "mobx"

import type { PortalsApprovalResponse, PortalsEstimateResponse, PortalsExecuteResponse } from "@/domains/portals/types"
import { MinimalPoolStore } from "@/domains/minimal-pool"
import { TokenStore } from "@/domains/tokens"

export type ZapState =
  | "idle"
  | "gettingEstimate"
  | "estimated"
  | "gettingApprovalStatus"
  | "approvalRequired"
  | "approving"
  | "approved"
  | "executing"
  | "completed"
  | "estimateFailed"
  | "approvalFailed"
  | "executionFailed"

export interface ZapStoreDAO {
  state: ZapState
  pool: MinimalPoolStore | null
  inputAmount: string
  estimate: PortalsEstimateResponse | null
  approvalResponse: PortalsApprovalResponse | null
  executionResponse: PortalsExecuteResponse | null
  estimateError: string | null
  approvalError: string | null
  executionError: string | null
  approvalTxHash: string | null
  executionTxHash: string | null
  uiToken: TokenStore | null
}

export class ZapStore {
  state: ZapState = "idle"
  pool: MinimalPoolStore | null = null
  inputAmount: string = "0"
  estimate: PortalsEstimateResponse | null = null
  approvalResponse: PortalsApprovalResponse | null = null
  executionResponse: PortalsExecuteResponse | null = null
  estimateError: string | null = null
  approvalError: string | null = null
  executionError: string | null = null
  approvalTxHash: string | null = null
  executionTxHash: string | null = null
  uiZapToken: TokenStore | null = null

  constructor(data?: Partial<ZapStoreDAO>) {
    // Debug: Log constructor data
    // console.log("data", data)

    if (data) {
      this.state = data.state ?? "idle"
      this.pool = data.pool ?? null
      this.inputAmount = data.inputAmount ?? "0"
      this.estimate = data.estimate ?? null
      this.approvalResponse = data.approvalResponse ?? null
      this.executionResponse = data.executionResponse ?? null
      this.estimateError = data.estimateError ?? null
      this.approvalError = data.approvalError ?? null
      this.executionError = data.executionError ?? null
      this.approvalTxHash = data.approvalTxHash ?? null
      this.executionTxHash = data.executionTxHash ?? null
    }

    makeObservable(this, {
      approvalError: observable,
      approvalResponse: observable,
      approvalTxHash: observable,
      estimate: observable,
      estimateError: observable,
      executionError: observable,
      executionResponse: observable,
      executionTxHash: observable,
      inputAmount: observable,
      pool: observable,
      state: observable,
      uiZapToken: observable,
      clearAllErrors: action,
      clearApprovalError: action,
      clearEstimateError: action,
      clearExecutionError: action,
      canExecute: computed,
      canGetEstimate: computed,
      displayInputAmount: computed,
      hasError: computed,
      isInProgress: computed,
      zapToken: computed,
      reset: action,
      setApprovalError: action,
      setApprovalTxHash: action,
      setEstimateError: action,
      setExecutionError: action,
      setExecutionTxHash: action,
      setInputAmount: action,
      setPool: action,
      setState: action,
      setUiZapToken: action,
      updateApprovalResponse: action,
      updateEstimate: action,
      updateExecutionResponse: action,
    })
  }

  clearAllErrors(): void {
    this.estimateError = null
    this.approvalError = null
    this.executionError = null
  }

  clearApprovalError(): void {
    this.approvalError = null
  }

  clearEstimateError(): void {
    this.estimateError = null
  }

  clearExecutionError(): void {
    this.executionError = null
  }

  setApprovalError(error: string): void {
    this.approvalError = error
  }

  setApprovalTxHash(txHash: string): void {
    this.approvalTxHash = txHash
  }

  setInputAmount(amount: string): void {
    this.inputAmount = amount
  }

  setPool(pool: MinimalPoolStore): void {
    this.pool = pool
  }

  setState(state: ZapState): void {
    this.state = state
  }

  setEstimateError(error: string): void {
    this.estimateError = error
  }

  setExecutionError(error: string): void {
    this.executionError = error
  }

  setExecutionTxHash(txHash: string): void {
    this.executionTxHash = txHash
  }

  updateApprovalResponse(approvalResponse: PortalsApprovalResponse): void {
    this.approvalResponse = approvalResponse
    this.clearApprovalError()
  }

  updateEstimate(estimate: PortalsEstimateResponse): void {
    this.estimate = estimate
    this.clearEstimateError()
  }

  updateExecutionResponse(executionResponse: PortalsExecuteResponse): void {
    this.executionResponse = executionResponse
    this.clearExecutionError()
  }

  setUiZapToken(token: TokenStore): void {
    this.uiZapToken = token
  }

  clearUiZapToken(): void {
    this.uiZapToken = null
  }

  reset(): void {
    this.state = "idle"
    this.inputAmount = "0"
    this.estimate = null
    this.approvalResponse = null
    this.executionResponse = null
    this.estimateError = null
    this.approvalError = null
    this.executionError = null
    this.approvalTxHash = null
    this.executionTxHash = null
  }

  get approvalTxHashUrl(): string {
    if (!this.approvalTxHash) return ""
    return `https://basescan.org/tx/${this.approvalTxHash}`
  }

  get canGetEstimate(): boolean {
    return this.state === "idle" && this.pool !== null && Number(this.inputAmount) > 0
  }

  get canExecute(): boolean {
    return (
      (this.state === "estimated" || this.state === "approved") &&
      this.estimate !== null &&
      this.pool !== null &&
      Number(this.inputAmount) > 0
    )
  }

  get displayInputAmount(): string {
    if (!this.zapToken) return "0"
    return this.inputAmount
  }

  get executionTxHashUrl(): string {
    if (!this.executionTxHash) return ""
    return `https://basescan.org/tx/${this.executionTxHash}`
  }

  get hasError(): boolean {
    return this.estimateError !== null || this.approvalError !== null || this.executionError !== null
  }

  get inputAmountBigint(): bigint {
    if (!this.zapToken) return 0n
    return this.zapToken.parseUnits(this.inputAmount)
  }

  get isInProgress(): boolean {
    return ["gettingEstimate", "approvalRequired", "approving", "executing"].includes(this.state)
  }

  get zapToken(): TokenStore | null {
    if (this.uiZapToken) {
      return this.uiZapToken
    }

    if (!this.pool) {
      return null
    }

    const preferredZapSymbols = ["usdc", "weth", "scale", "cbbtc", "frxusd"]

    if (preferredZapSymbols.includes(this.pool.token0.symbol.toLowerCase() ?? "")) {
      return this.pool.token0
    }
    if (preferredZapSymbols.includes(this.pool.token1.symbol.toLowerCase() ?? "")) {
      return this.pool.token1
    }

    return this.pool.token0
  }
}
